import { useState } from 'react'
import { Avatar, Card, Typography, Space, Spin, Button, message as antMessage } from 'antd'
import { User, Bot, Copy, Check } from 'lucide-react'
import type { Message as MessageType, FollowUpAction } from '@/types/chat.types'
import { SourcesList } from '../SourcesList'
import { AgentActivity } from '../AgentActivity'
import { MarkdownWithSources } from '../MarkdownWithSources'
import { FollowUpActions } from '../FollowUpActions'
import { CopyActionModal } from '../CopyActionModal'
import { t } from '@/config/translations'
import styles from './Message.module.css'

const { Text } = Typography

export interface MessageProps {
  message: MessageType
  isStreaming?: boolean
  onSendMessage?: (query: string) => void
}

export function Message({ message, isStreaming = false, onSendMessage }: MessageProps) {
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'
  const isLoading = isAssistant && message.content.length === 0 && isStreaming
  const [copied, setCopied] = useState(false)
  const [copyModalOpen, setCopyModalOpen] = useState(false)
  const [copyModalContent, setCopyModalContent] = useState({ title: '', content: '' })

  // Get className safely
  const messageClassName = isUser ? styles['userMessage'] : styles['assistantMessage']
  const cardProps = messageClassName ? { className: messageClassName } : {}

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      void antMessage.success(t('message_copied'))
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      void antMessage.error(t('copy_failed'))
    }
  }

  const handleActionClick = (action: FollowUpAction) => {
    if (action.type === 'chat_query') {
      // Send the query as a new message
      if (onSendMessage) {
        onSendMessage(action.payload)
      }
    } else {
      // type === 'copy_text', open modal with draft text
      setCopyModalContent({
        title: action.label,
        content: action.payload,
      })
      setCopyModalOpen(true)
    }
  }

  return (
    <div className={isUser ? styles['userMessageContainer'] : styles['assistantMessageContainer']}>
      <Space align="start" size={12}>
        {/* Avatar */}
        <Avatar
          size={36}
          icon={isUser ? <User size={20} /> : <Bot size={20} />}
          style={{
            backgroundColor: isUser ? '#1890ff' : '#52c41a',
            flexShrink: 0,
          }}
        />

        {/* Message Content */}
        <div className={styles['messageWrapper']}>
          <Card
            size="small"
            variant="outlined"
            {...cardProps}
            styles={{
              body: {
                padding: '12px 16px',
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                msUserSelect: 'text',
              },
            }}
            style={{
              userSelect: 'text',
              WebkitUserSelect: 'text',
              MozUserSelect: 'text',
              msUserSelect: 'text',
            }}
          >
            {/* Scenario badge for user messages */}
            {isUser && message.scenario && message.scenario !== 'default' && (
              <Text
                type="secondary"
                style={{
                  fontSize: '12px',
                  display: 'block',
                  marginBottom: '8px',
                }}
              >
                Scenario: {message.scenario}
              </Text>
            )}

            {/* Message content */}
            {isLoading ? (
              <Space size={8}>
                <Spin size="small" />
                <Text type="secondary">{t('generating_response')}</Text>
              </Space>
            ) : isAssistant ? (
              <MarkdownWithSources
                content={message.content}
                sources={message.sources || undefined}
                scenario={message.scenario}
              />
            ) : (
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'normal',
                  overflowWrap: 'break-word',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text',
                }}
              >
                {message.content}
              </div>
            )}

            {/* Agent activity */}
            {isAssistant && message.agentActivity && (
              <AgentActivity activity={message.agentActivity} />
            )}

            {/* Sources list */}
            {isAssistant && message.sources && message.sources.length > 0 && (
              <SourcesList sources={message.sources} />
            )}
          </Card>

          {/* Copy button for assistant messages */}
          {isAssistant && !isLoading && message.content && (
            <Button
              type="text"
              size="small"
              icon={copied ? <Check size={14} /> : <Copy size={14} />}
              onClick={() => {
                void handleCopy()
              }}
              className={styles['copyButton'] || undefined}
              title={t('copy_message')}
            />
          )}
        </div>
      </Space>

      {/* Follow-up actions - OUTSIDE the message card */}
      {isAssistant && !isLoading && message.actions && message.actions.length > 0 && (
        <div className={styles['actionsContainer']}>
          <FollowUpActions
            actions={message.actions}
            onActionClick={handleActionClick}
            disabled={isStreaming}
          />
        </div>
      )}

      {/* Copy Action Modal */}
      <CopyActionModal
        open={copyModalOpen}
        title={copyModalContent.title}
        content={copyModalContent.content}
        onClose={() => {
          setCopyModalOpen(false)
        }}
      />
    </div>
  )
}
